import { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize2, Move } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { ASPECT_RATIOS } from './editorConfig';

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export const VideoPreviewPanel = ({ 
  videoUrl, 
  settings, 
  currentTime = 0,
  onTimeUpdate,
  textTracks = [],
  onTextTracksChange,
  playToggleRef = null,
  onPlayStateChange = null,
  clips = null,
  musicClipInfo = null,  // {startTime, endTime, volume, sourceUrl} from timeline
  className = '' 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);

  // Helper: update both local state and notify parent
  const updatePlaying = useCallback((val) => {
    setIsPlaying(val);
    onPlayStateChange?.(val);
  }, [onPlayStateChange]);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [editingTextId, setEditingTextId] = useState(null);
  const [draggingTextId, setDraggingTextId] = useState(null);
  const dragRef = useRef(null);
  const previewRef = useRef(null);
  const videoRef = useRef(null);
  const musicRef = useRef(null);
  const isInternalUpdateRef = useRef(false); // prevents feedback loop

  const startT = settings.startTime || 0;
  const endT = settings.endTime > startT ? settings.endTime : null;

  // Get aspect ratio dimensions
  const aspectRatio = ASPECT_RATIOS.find(ar => ar.id === settings.aspectRatio) || ASPECT_RATIOS[1];

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const effectiveDuration = endT
    ? endT - startT
    : duration;

  // Effect: seek video when external currentTime changes (e.g. from slider) — ONLY when paused
  useEffect(() => {
    const v = videoRef.current;
    if (!v || isInternalUpdateRef.current || isPlaying) return;
    const target = startT + currentTime;
    if (Math.abs(v.currentTime - target) > 0.2) {
      v.currentTime = target;
    }
  }, [currentTime, startT, isPlaying]);

  const lastTimeUpdateRef = useRef(0);
  const rafRef = useRef(null);

  // Effect: mute sync
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.muted = isMuted;
  }, [isMuted]);

  // rAF loop for smooth timeline progress during playback (replaces onTimeUpdate→setState)
  useEffect(() => {
    if (!isPlaying) { cancelAnimationFrame(rafRef.current); return; }
    const tick = () => {
      const v = videoRef.current;
      if (!v || v.paused) { updatePlaying(false); return; }
      const now = Date.now();
      if (now - lastTimeUpdateRef.current >= 80) {
        lastTimeUpdateRef.current = now;
        // Hard stop at endTime
        if (endT && v.currentTime >= endT) {
          v.pause(); v.currentTime = startT;
          updatePlaying(false); onTimeUpdate?.(0); return;
        }
        const relTime = Math.max(0, v.currentTime - startT);
        // ── Clip-aware playback: skip deleted segments ──────────────────────
        if (clips && clips.length > 0) {
          const sorted = [...clips].sort((a, b) => a.startTime - b.startTime);
          const inAClip = sorted.some(c => relTime >= c.startTime && relTime < c.endTime);
          if (!inAClip) {
            // Jump to start of next active clip
            const next = sorted.find(c => c.startTime > relTime);
            if (next) {
              v.currentTime = startT + next.startTime;
            } else {
              // Past last clip — stop
              v.pause(); v.currentTime = startT;
              updatePlaying(false); onTimeUpdate?.(0); return;
            }
          }
        }
        onTimeUpdate?.(Math.max(0, v.currentTime - startT));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, startT, endT, onTimeUpdate, clips]);

  // DIRECT play/pause — must be called synchronously in user gesture for audio to work in Chrome
  const handlePlayPause = useCallback(() => {
    const v = videoRef.current;
    const m = musicRef.current;
    if (!v) return;
    if (v.paused) {
      v.play()
        .then(() => {
          updatePlaying(true);
          // Sync music: start from the correct position in the song
          if (m && musicClipInfo?.sourceUrl) {
            const relTime = Math.max(0, v.currentTime - (settings?.startTime || 0));
            const musicOffset = relTime - (musicClipInfo.startTime || 0);
            if (musicOffset >= 0) {
              m.currentTime = musicOffset % (m.duration || 99999);
              m.volume = musicClipInfo.volume ?? 0.7;
              m.play().catch(() => {});
            }
          }
        })
        .catch(() => updatePlaying(false));
    } else {
      v.pause();
      if (m) m.pause();
      updatePlaying(false);
    }
  }, [updatePlaying, settings, musicClipInfo]);

  // Expose handlePlayPause to parent via ref (so TimelinePanel can trigger real video play)
  useEffect(() => {
    if (playToggleRef) playToggleRef.current = handlePlayPause;
  }, [playToggleRef, handlePlayPause]);

  // Pause music when video stops (e.g. on endTime)
  useEffect(() => {
    if (!isPlaying && musicRef.current) musicRef.current.pause();
  }, [isPlaying]);

  // Load / unload music when clip changes
  useEffect(() => {
    const m = musicRef.current;
    if (!m) return;
    const url = musicClipInfo?.sourceUrl || settings?.musicUrl;
    if (url) {
      if (m.src !== url) { m.src = url; m.volume = musicClipInfo?.volume ?? 0.7; m.load(); }
    } else {
      m.pause(); m.src = '';
    }
  }, [musicClipInfo, settings?.musicUrl]);

  // Keep volume in sync when clip volume changes (without reloading)
  useEffect(() => {
    const m = musicRef.current;
    if (m && musicClipInfo?.volume !== undefined) m.volume = musicClipInfo.volume;
  }, [musicClipInfo?.volume]);

  const handleVideoTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    // Throttle UI updates to ~10fps to avoid triggering too many React re-renders
    const now = Date.now();
    // Stop at endTime if clip has one
    if (endT && v.currentTime >= endT) {
      v.pause();
      v.currentTime = startT;
      updatePlaying(false);
      isInternalUpdateRef.current = true;
      onTimeUpdate?.(0);
      setTimeout(() => { isInternalUpdateRef.current = false; }, 80);
      lastTimeUpdateRef.current = 0;
      return;
    }
    if (now - lastTimeUpdateRef.current < 100) return; // 10fps cap
    lastTimeUpdateRef.current = now;
    const relTime = Math.max(0, v.currentTime - startT);
    isInternalUpdateRef.current = true;
    onTimeUpdate?.(relTime);
    setTimeout(() => { isInternalUpdateRef.current = false; }, 80);
  }, [startT, endT, onTimeUpdate]);

  const handleLoadedMetadata = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = 1;   // ensure max volume
    v.muted = false; // ensure unmuted regardless of any browser default
    setDuration(v.duration);
    if (startT > 0) v.currentTime = startT;
  }, [startT]);

  // Text overlay drag handlers
  const handleTextMouseDown = useCallback((e, textId) => {
    e.stopPropagation();
    if (editingTextId === textId) return;
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;
    const text = textTracks.find(t => t.id === textId);
    if (!text) return;
    setDraggingTextId(textId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: text.position?.x ?? 50, origY: text.position?.y ?? 50, rect };

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const { rect, startX, startY, origX, origY } = dragRef.current;
      const dx = ((ev.clientX - startX) / rect.width) * 100;
      const dy = ((ev.clientY - startY) / rect.height) * 100;
      onTextTracksChange?.(prev => prev.map(t =>
        t.id === textId ? { ...t, position: { x: clamp(origX + dx, 0, 100), y: clamp(origY + dy, 0, 100) } } : t
      ));
    };
    const onUp = () => {
      setDraggingTextId(null);
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [editingTextId, textTracks, onTextTracksChange]);

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Preview Container */}
      <div 
        ref={previewRef}
        className="relative rounded-xl overflow-hidden mx-auto"
        style={{ 
          background: settings.background === 'solid' 
            ? settings.backgroundColor 
            : '#000000',
          aspectRatio: `${aspectRatio.width} / ${aspectRatio.height}`,
          ...(aspectRatio.height > aspectRatio.width
            ? { height: '60vh', maxHeight: '60vh', width: 'auto', maxWidth: '100%' }
            : { width: '100%', maxWidth: '100%', height: 'auto' }
          ),
        }}
      >
        {/* Hidden audio element for background music */}
        <audio ref={musicRef} loop preload="none" style={{ display: 'none' }} />

        {/* Background blur effect — FIRST in DOM = renders behind everything else */}
        {settings.background === 'blur' && videoUrl && (
          <video
            src={videoUrl}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', filter: 'blur(18px)', transform: 'scale(1.12)',
              pointerEvents: 'none',
            }}
            muted autoPlay loop playsInline
          />
        )}

        {/* Video content — z-index 1 ensures it's above the blur background */}
        <div className="absolute inset-0 flex items-center justify-center">
          {videoUrl ? (
            <video
              ref={videoRef}
              key={videoUrl}
              src={videoUrl}
              className="w-full h-full object-contain"
              controls={false}
              playsInline
              preload="auto"
              onTimeUpdate={handleVideoTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => updatePlaying(false)}
            />
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-2"
                style={{ background: 'hsl(var(--surface))' }}>
                <Play size={24} style={{ color: 'hsl(var(--foreground-subtle))' }} />
              </div>
              <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>
                Sin vídeo cargado
              </p>
            </div>
          )}
        </div>

        {/* Zoom indicator */}
        {settings.zoomEnabled && settings.zoom > 1 && (
          <div className="absolute top-2 left-2 px-2 py-1 rounded text-xs"
            style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
            {settings.zoom.toFixed(1)}x zoom
          </div>
        )}

        {/* Hook text overlay */}
        {settings.hookText && (
          <div className={`absolute left-0 right-0 p-4 text-center ${
            settings.hookPosition === 'top' ? 'top-4' : 
            settings.hookPosition === 'center' ? 'top-1/2 -translate-y-1/2' : 'bottom-16'
          }`}>
            <span className="px-3 py-2 rounded-lg text-lg font-bold"
              style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}>
              {settings.hookText}
            </span>
          </div>
        )}

        {/* Caption preview — hidden when real subtitle tracks exist */}
        {settings.captionsEnabled && !textTracks.some(t => t.isSubtitle) && (() => {
          const styleMap = {
            minimal: { background: 'transparent', color: settings.captionColor || '#fff', fontWeight: 400, fontSize: '13px', textShadow: 'none', padding: '2px 6px', borderRadius: '4px' },
            bold:    { background: 'rgba(0,0,0,0.6)', color: settings.captionColor || '#ffff00', fontWeight: 800, fontSize: '22px', textShadow: '0 2px 4px rgba(0,0,0,0.8)', padding: '4px 10px', borderRadius: '6px' },
            karaoke: { background: 'hsl(var(--primary))', color: settings.captionColor || '#fff', fontWeight: 700, fontSize: '18px', textShadow: 'none', padding: '3px 10px', borderRadius: '4px' },
            boxed:   { background: 'rgba(0,0,0,0.85)', color: settings.captionColor || '#fff', fontWeight: 600, fontSize: '16px', textShadow: 'none', padding: '6px 14px', borderRadius: '4px' },
            outline: { background: 'transparent', color: settings.captionColor || '#fff', fontWeight: 700, fontSize: '20px', textShadow: '-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000', padding: '4px 8px', borderRadius: '4px' },
          };
          const fontFamilyMap = {
            default: 'system-ui, sans-serif',
            impact: '"Impact", "Arial Black", sans-serif',
            'arial-bold': '"Arial", "Helvetica Neue", sans-serif',
            georgia: '"Georgia", serif',
            courier: '"Courier New", monospace',
          };
          const cs = styleMap[settings.captionStyle] || styleMap.bold;
          const posClass = settings.captionPosition === 'top' ? 'top-4' : settings.captionPosition === 'center' ? 'top-1/2 -translate-y-1/2' : 'bottom-16';
          const sampleText = settings.captionStyle === 'karaoke' ? (
            <span>
              <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '3px', padding: '0 2px' }}>¡Este</span>{' '}
              <span style={{ background: '#fff', color: 'hsl(var(--primary))', borderRadius: '3px', padding: '0 2px', fontWeight: 800 }}>momento</span>{' '}
              <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '3px', padding: '0 2px' }}>es viral!</span>
            </span>
          ) : 'Sample caption text...';
          return (
            <div className={`absolute left-0 right-0 px-4 text-center ${posClass}`}>
              <span style={{ ...cs, fontFamily: fontFamilyMap[settings.captionFont] || fontFamilyMap.default, display: 'inline-block' }}>
                {sampleText}
              </span>
            </div>
          );
        })()}

        {/* CTA overlay */}
        {settings.ctaText && (
          <div className="absolute bottom-4 left-0 right-0 p-4 text-center">
            <span className="px-4 py-2 rounded-full text-sm font-semibold"
              style={{ background: 'hsl(var(--primary))', color: '#fff' }}>
              {settings.ctaText}
            </span>
          </div>
        )}

        {/* Watermark indicator */}
        {settings.brandingEnabled && (
          <div className={`absolute p-2 ${
            settings.watermarkPosition === 'top-left' ? 'top-2 left-2' :
            settings.watermarkPosition === 'top-right' ? 'top-2 right-2' :
            settings.watermarkPosition === 'bottom-left' ? 'bottom-12 left-2' :
            'bottom-12 right-2'
          }`}>
            <div className="px-2 py-1 rounded text-xs"
              style={{ 
                background: 'rgba(255,255,255,0.2)', 
                color: '#fff',
                opacity: settings.watermarkOpacity / 100
              }}>
              WATERMARK
            </div>
          </div>
        )}

        {/* Aspect ratio label */}
        <div className="absolute top-2 right-2 px-2 py-1 rounded text-xs"
          style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
          {settings.aspectRatio}
        </div>

        {/* Text & subtitle overlays — draggable, editable */}
        {textTracks.filter(t => currentTime >= t.startTime && currentTime < t.endTime).map(t => {
          const fontFamilyMap = { default: 'system-ui, sans-serif', impact: '"Impact","Arial Black",sans-serif', 'arial-bold': '"Arial","Helvetica Neue",sans-serif', georgia: '"Georgia",serif', courier: '"Courier New",monospace' };
          const isEditing = editingTextId === t.id;
          const isDragging = draggingTextId === t.id;
          return (
            <div key={t.id}
              style={{
                position: 'absolute',
                left: `${t.position?.x ?? 50}%`,
                top: `${t.position?.y ?? 50}%`,
                transform: 'translate(-50%, -50%)',
                cursor: isEditing ? 'text' : isDragging ? 'grabbing' : 'grab',
                zIndex: 30,
                maxWidth: '90%',
                userSelect: isEditing ? 'text' : 'none',
              }}
              onMouseDown={e => { if (!isEditing) handleTextMouseDown(e, t.id); }}
              onDoubleClick={e => { e.stopPropagation(); setEditingTextId(t.id); }}
            >
              {/* Move indicator */}
              {!isEditing && (
                <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: 'hsl(var(--primary))', borderRadius: 4, padding: '1px 4px', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Move size={8} style={{ color: '#fff' }} />
                  <span style={{ fontSize: 8, color: '#fff' }}>doble-click=editar</span>
                </div>
              )}
              {isEditing ? (
                <input
                  autoFocus
                  value={t.text}
                  onChange={e => onTextTracksChange?.(prev => prev.map(tx => tx.id === t.id ? { ...tx, text: e.target.value } : tx))}
                  onFocus={e => e.target.select()}
                  onBlur={() => setEditingTextId(null)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') { e.stopPropagation(); setEditingTextId(null); } }}
                  style={{
                    background: 'rgba(0,0,0,0.4)', border: '1px solid hsl(var(--primary))', borderRadius: 4,
                    color: t.color || '#fff', fontFamily: fontFamilyMap[t.fontFamily || 'default'],
                    fontSize: `${t.fontSize || 18}px`, fontWeight: 700,
                    padding: '2px 8px', outline: 'none', width: 'auto', minWidth: '80px',
                    textAlign: t.textAlign || 'center',
                  }}
                />
              ) : (
                <span style={{
                  display: 'block', color: t.color || '#fff',
                  fontFamily: fontFamilyMap[t.fontFamily || 'default'],
                  fontSize: `${t.fontSize || 18}px`, fontWeight: 700,
                  textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
                  padding: '2px 8px',
                  background: t.isSubtitle ? 'rgba(0,0,0,0.5)' : 'transparent',
                  borderRadius: 4,
                  outline: '1px dashed rgba(255,255,255,0.4)',
                  textAlign: t.textAlign || 'center',
                  minWidth: '80px',
                }}>
                  {t.text}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Playback controls */}
      <div className="mt-4 p-3 rounded-lg" style={{ background: 'hsl(var(--surface))' }}>
        {/* Progress bar */}
        <div className="mb-3">
          <Slider
            value={[currentTime]}
            min={0}
            max={effectiveDuration || 100}
            step={0.1}
            onValueChange={([val]) => {
              const v = videoRef.current;
              if (v) v.currentTime = startT + val;
              onTimeUpdate?.(val);
            }}
            className="cursor-pointer"
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
              {formatTime(currentTime)}
            </span>
            <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
              {formatTime(effectiveDuration)}
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              data-testid="play-pause-btn"
              onClick={handlePlayPause}
              className="p-2 rounded-lg hover:bg-surface-hover"
              style={{ color: 'hsl(var(--foreground))' }}
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="p-2 rounded-lg hover:bg-surface-hover"
              style={{ color: 'hsl(var(--foreground-muted))' }}
            >
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>
          <button
            className="p-2 rounded-lg hover:bg-surface-hover"
            style={{ color: 'hsl(var(--foreground-muted))' }}
          >
            <Maximize2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoPreviewPanel;
